import * as React from "react";
import apiClient from "@/api-client";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import { SMB_PLATFORM } from "@/runtime-env";
import { appPathname, goTo, IN_SMB } from "@/lib/platform";

import { useConfirmEmailVerification, useSendEmailVerification } from "@/hooks/api/use-email-verification";
import { fetchOnboardingState, isOnboardingIncomplete, onboardingKeys } from "@/hooks/api/use-onboarding";

export interface User {
	id: string;
	email: string;
	name: string;
	avatar?: string;
	role?: string;
	isSuperAdmin?: boolean;
	isEmailVerified?: boolean;
	otpEnabled?: boolean;
}

export interface Account {
	id: string;
	name: string;
	type?: "smb" | "enterprise";
}

export interface Permissions {
	[key: string]: boolean | string; 
}

interface AuthContextType {
	user: User | null;
	account: Account | null;
	isLoading: boolean;
	/** The profile could not be loaded while the session is still valid (network down, backend deploying). */
	hasLoadError: boolean;
	isAuthenticated: boolean;
	isEmailVerified: boolean;
	startUrl: string;
	setStartUrl: (url: string) => void;
	getMe: (withRedirect?: boolean) => Promise<void>;
	setUser: (user: User | null) => void;
	setAccount: (account: Account | null) => void;
	sendEmailVerification: () => Promise<void>;
	confirmEmailVerification: (token: string) => Promise<void>;
	permissions: Permissions;
	setPermissions: (permissions: Permissions) => void;
}

const AuthContext = React.createContext<AuthContextType | undefined>(undefined);

export function useAuth(): AuthContextType {
	const context = React.useContext(AuthContext);
	if (context === undefined) {
		throw new Error("useAuth must be used within an AuthProvider");
	}
	return context;
}

export function AuthProvider({ children }: { children: React.ReactNode }): React.JSX.Element {
	const [user, setUser] = React.useState<User | null>(null);
	const [account, setAccount] = React.useState<Account | null>(null);
	const [permissions, setPermissions] = React.useState<Permissions>({});
	const [isLoading, setIsLoading] = React.useState(true);
	const [hasLoadError, setHasLoadError] = React.useState(false);
  const [startUrl, setStartUrl] = React.useState<string>(() => {
    const pathname = appPathname();
    const search = globalThis.location.search;

    if (pathname === "/") {
      return "/";
    }

    if (pathname === "/sign-in" || pathname === "/sign-up" || pathname === "/forgot-password" || pathname === "/reset-password" || pathname === "/confirm-email") {
      return "/";
    }

    // Signing in on a self-service deployment loads the panel at its account list; that is not where the user was going.
    if (pathname === "/accounts") {
      return "/";
    }

    return pathname + search;
  });

	const navigate = useNavigate();
	const queryClient = useQueryClient();

	const isAuthenticated = !!user;
	const isEmailVerified = user?.isEmailVerified ?? false;

	const sendEmailVerificationMutation = useSendEmailVerification();
	const confirmEmailVerificationMutation = useConfirmEmailVerification();

	const accountIdRef = React.useRef<string | null>(null);
	const getMe = React.useCallback(async (withRedirect = false) => {
		setIsLoading(true);
		setHasLoadError(false);

		try {
			let response = await apiClient.get("/me");
			// With one account there is nothing to choose: enter it, and its type decides where to go.
			if (withRedirect && SMB_PLATFORM && !response.data.account && !response.data.user?.isSuperAdmin) {
				const available = await apiClient.get("/accounts/available");
				const [only, ...others] = available.data?.data ?? [];
				if (only && others.length === 0) {
					await apiClient.post(`/accounts/${only.id}/use`);
					response = await apiClient.get("/me");
				}
			}
			// Setup state is per account: another account's must not decide where this one goes.
			if (accountIdRef.current !== (response.data.account?.id ?? null)) {
				queryClient.removeQueries({ queryKey: onboardingKeys.all });
				accountIdRef.current = response.data.account?.id ?? null;
			}
			setUser(response.data.user);
			setAccount(response.data.account);
			setPermissions(response.data.permissions);

			if (withRedirect && response.data.account) {
				// Super admins always get the enterprise experience, even inside an
				// SMB-typed account (e.g. the platform workspace or support access).
				if (SMB_PLATFORM && response.data.account.type === "smb" && !response.data.user?.isSuperAdmin) {
					// Until the receptionist is live, an SMB account resumes the setup wizard.
					const onboarding = await fetchOnboardingState(queryClient);
					goTo(navigate, "smb", isOnboardingIncomplete(onboarding) ? "/onboarding" : "/");
				} else {
					goTo(navigate, "admin", "/");
				}
			}

      if (withRedirect && !response.data.account) {
        // The business app asks which account before anyone leaves it; a super admin picks in the dashboard.
        if (IN_SMB && !response.data.user?.isSuperAdmin) {
          navigate("/");
        } else {
          goTo(navigate, "admin", "/accounts");
        }
      }
		} catch (error) {
			// The interceptor (api-client.ts) is the only place that decides to sign the user out: it can tell an
			// auth rejection from a network failure. If it did, the token is gone and the page is on its way to
			// sign-in. If the token is still here, the session is fine (a 5xx, a timeout) and AuthGuard offers a retry.
			setUser(null);
			setPermissions({});
			setHasLoadError(Boolean(localStorage.getItem("token")));
			console.error("Failed to load user:", error);
		} finally {
			setIsLoading(false);
		}
	}, [navigate, queryClient]);

	const sendEmailVerification = React.useCallback(async () => {
		await sendEmailVerificationMutation.mutateAsync();
	}, [sendEmailVerificationMutation]);

	const confirmEmailVerification = React.useCallback(
		async (token: string) => {
			await confirmEmailVerificationMutation.mutateAsync({ token });

			setTimeout(async () => {
				// The link is often opened where nobody is signed in: there is no profile to route by.
				if (localStorage.getItem("token")) {
					await getMe(true);
				} else {
					navigate("/sign-in");
				}
			}, 2000);
		},
		[confirmEmailVerificationMutation, getMe, navigate]
	);

	React.useEffect(() => {
		const pathname = appPathname();
		if (
			pathname === "/sign-in" ||
			(IN_SMB && pathname === "/sign-up") ||
			pathname === "/forgot-password" ||
			pathname === "/reset-password" ||
			pathname === "/confirm-email"
		) {
			return;
		}
		getMe();
	}, [getMe]);

	const value: AuthContextType = {
		user,
		account,
		isLoading,
		hasLoadError,
		isAuthenticated,
		isEmailVerified,
		startUrl,
		setStartUrl,
		getMe,
		setUser,
		setAccount,
		sendEmailVerification,
		confirmEmailVerification,
		permissions,
		setPermissions,
	};

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
